/**
 * Google Analytics 4 connect for a granted client (flag connectors.ga4 on the client; agency owner or admin; client
 * grant with manage:campaigns). ClientGa4Connect sits on the client's analytics screen: the client's GA4 connections
 * with connect / reconnect / disconnect. Connect goes to Google, returns through the partners host's
 * /bff/oauth/connectors/callback to /integrations/connect, where PartnerConnectReturn hands `g4a_` states to
 * Ga4ReturnScreen (property picker) and every other state to the YouTube return screen. A 404 (flag off, no grant)
 * or 403 (member) hides the panel.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { GA4_AGENCY_STATE_PREFIX, type Ga4AgencyConnectionView } from '@qm/contracts/connectors-ga4';
import { Button, Heading, Panel, Text } from '@qm/ui';
import { BffError } from '../kit/http';
import { useSurface } from '../kit/context';
import { Badge, Notice, When } from '../kit/ui';
import { ga4ConnectApi } from '../api/ga4-connect';
import { YouTubeReturnScreen } from './youtube';

const P = 'connectors.ga4.agencyConnect';
const KNOWN = ['oauth_state_expired', 'missing_scopes', 'account_not_offered', 'provider_not_configured'] as const;
export function ga4ConnectErrorKey(code: string | null | undefined): string {
  if (code === 'access_denied' || code === 'user_denied') return `${P}.error.denied`;
  return (KNOWN as readonly string[]).includes(code ?? '') ? `${P}.error.${code}` : `${P}.error.generic`;
}
const codeOf = (e: unknown) => (e instanceof BffError ? ((e.envelope?.error.details as { code?: string } | undefined)?.code ?? null) : null);
const safeUrl = (u: unknown) => { try { return typeof u === 'string' && new URL(u).protocol === 'https:' ? u : null; } catch { return null; } };
const STATE_RE = new RegExp(`^${GA4_AGENCY_STATE_PREFIX}[A-Za-z0-9_-]{1,60}$`);

/** Which return screen handles an OAuth state arriving on the partners host. */
export function returnScreenFor(state: string | null | undefined): 'ga4' | 'youtube' {
  return state?.startsWith(GA4_AGENCY_STATE_PREFIX) ? 'ga4' : 'youtube';
}

function useGa4ConnectApi() {
  const { http } = useSurface();
  return useMemo(() => ga4ConnectApi(http), [http]);
}

export function ClientGa4Connect({ tenantId }: { tenantId: string }) {
  const { t } = useSurface();
  const api = useGa4ConnectApi();
  const [rows, setRows] = useState<Ga4AgencyConnectionView[] | null>(null);
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = () => api.connections(tenantId).then(setRows, (e: unknown) => {
    if (e instanceof BffError && (e.status === 404 || e.status === 403)) setHidden(true); else setError(`${P}.error.generic`);
  });
  useEffect(() => { void load(); }, [tenantId]);
  if (hidden) return null;

  const start = async (reconnectConnectionId?: string) => {
    setBusy(true); setError(null);
    try {
      const url = safeUrl((await api.start(tenantId, reconnectConnectionId)).authorizeUrl);
      if (!url) throw new Error('bad authorize url');
      window.location.assign(url);
    } catch (e) { setError(ga4ConnectErrorKey(codeOf(e))); setBusy(false); }
  };
  const disconnect = async (id: string) => {
    setBusy(true); setError(null);
    try { await api.disconnect(tenantId, id); await load(); } catch (e) { setError(ga4ConnectErrorKey(codeOf(e))); }
    setBusy(false);
  };

  return (
    <Panel>
      <div data-testid="client-ga4-connect">
        <Heading level={3}>{t(`${P}.title`)}</Heading>
        <Text tone="muted">{t(`${P}.intro`)}</Text>
        <Text tone="muted">{t(`${P}.readOnly`)}</Text>
        {error ? <Notice tone="bad">{t(error)}</Notice> : null}
        {rows && !rows.length ? <Text>{t(`${P}.empty`)}</Text> : null}
        {rows?.map((r) => (
          <div key={r.id} data-testid={`client-ga4-${r.id}`}>
            <Text>
              <bdi>{r.displayName ?? r.accountRef}</bdi>{' '}
              <Badge tone={r.action === 'reconnect' ? 'bad' : r.action === 'wait' ? 'warn' : 'good'}>{t(`${P}.status.${r.action === 'none' ? 'connected' : r.action}`)}</Badge>
              {r.lastSuccessAt ? <> · <When iso={r.lastSuccessAt} /></> : null}
            </Text>
            {r.action === 'reconnect' ? <Button onClick={() => void start(r.id)} disabled={busy}>{t(`${P}.reconnect`)}</Button> : null}
            <Button onClick={() => void disconnect(r.id)} disabled={busy}>{t(`${P}.disconnect`)}</Button>
          </div>
        ))}
        <Button onClick={() => void start()} disabled={busy || rows === null}>{t(`${P}.connect`)}</Button>
      </div>
    </Panel>
  );
}

/** Partners host /integrations/connect for `g4a_` states: finish the connection, pick properties when offered. */
export function Ga4ReturnScreen() {
  const { t } = useSurface();
  const api = useGa4ConnectApi();
  const [phase, setPhase] = useState<'working' | 'choose' | 'error'>('working');
  const [errorKey, setErrorKey] = useState(`${P}.error.generic`);
  const [pending, setPending] = useState<{ id: string; tenantId: string; properties: { ref: string; name: string | null }[] } | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const started = useRef(false);
  const fail = (code: string | null | undefined) => { setErrorKey(ga4ConnectErrorKey(code)); setPhase('error'); };
  const back = (tenantId: string) => window.location.assign(`/clients/${encodeURIComponent(tenantId)}/analytics`);

  useEffect(() => {
    if (typeof window === 'undefined' || started.current) return;
    started.current = true;
    const p = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    history.replaceState(null, '', window.location.pathname);
    const state = p.get('state') ?? '';
    const code = p.get('code') ?? '';
    if (p.get('error') || !STATE_RE.test(state) || !code || code.length > 2048) { fail(p.get('error')); return; }
    api.callback(state, code).then((r) => {
      if (!r.pendingId) { back(r.tenantId); return; }
      setPending({ id: r.pendingId, tenantId: r.tenantId, properties: r.properties });
      if (r.properties.length === 1) setChosen(new Set([r.properties[0]!.ref]));
      setPhase('choose');
    }, (e: unknown) => fail(codeOf(e)));
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!pending || !chosen.size) return;
    try { const r = await api.select(pending.id, [...chosen]); back(r.tenantId); } catch (err) { fail(codeOf(err)); }
  };
  const toggle = (ref: string, on: boolean) => setChosen((s) => { const x = new Set(s); if (on) x.add(ref); else x.delete(ref); return x; });

  return (
    <section aria-labelledby="ga4-return-title" data-testid="ga4-return">
      <Heading level={1} id="ga4-return-title">{t(`${P}.title`)}</Heading>
      {phase === 'working' ? <Text role="status">{t(`${P}.working`)}</Text> : null}
      {phase === 'error' ? (
        <div role="alert">
          <Notice tone="bad">{t(errorKey)}</Notice>
          <Button onClick={() => window.location.assign('/clients')}>{t(`${P}.back`)}</Button>
        </div>
      ) : null}
      {phase === 'choose' && pending ? (
        <form onSubmit={(e) => void submit(e)}>
          <fieldset>
            <legend>{t(`${P}.pick.title`)}</legend>
            {pending.properties.map((p, i) => (
              <div key={p.ref}>
                <input type="checkbox" id={`pga4-${i}`} checked={chosen.has(p.ref)} onChange={(ev) => toggle(p.ref, ev.target.checked)} />
                <label htmlFor={`pga4-${i}`}><bdi>{p.name ?? p.ref}</bdi> (<bdi>{p.ref}</bdi>)</label>
              </div>
            ))}
          </fieldset>
          <Button type="submit" variant="primary" disabled={!chosen.size}>{t(`${P}.pick.save`)}</Button>
        </form>
      ) : null}
    </section>
  );
}

/** Partners host /integrations/connect: GA4 agency states (`g4a_`) here, everything else to the YouTube return. */
export function PartnerConnectReturn() {
  const [which] = useState(() => (typeof window === 'undefined' ? 'youtube' : returnScreenFor(new URLSearchParams(window.location.hash.replace(/^#/, '')).get('state'))));
  return which === 'ga4' ? <Ga4ReturnScreen /> : <YouTubeReturnScreen />;
}
